import React, { useState, useMemo } from 'react';
import { getDayName, formatTemp, formatTempShort, getWeatherIcon, formatDate, filterForecastDays, DEGREE_SYMBOL } from '../../utils/weatherUtils';
import Icon from '../../utils/icons';
import './WeeklyForecast.css';

function WeeklyForecast({ data }) {
  const [selectedIndex, setSelectedIndex] = useState(null);

  // Filter out past days (timezone-safe) and limit to 5
  const filteredDays = useMemo(() => filterForecastDays(data?.days, 5), [data?.days]);

  if (!filteredDays || filteredDays.length === 0) {
    return (
      <div className="weekly-forecast">
        <div className="card-header">
          <h2 className="card-title">
            <Icon name="calendar" size={18} /> 5-Day Forecast
          </h2>
        </div>
        <div className="no-data">No forecast data available</div>
      </div>
    );
  }

  // Calculate temperature range for visualization
  const allTemps = filteredDays.flatMap(d => [d.temperature_min, d.temperature_max]);
  const minTemp = Math.min(...allTemps);
  const maxTemp = Math.max(...allTemps);
  const tempRange = maxTemp - minTemp || 1;

  const getTempPosition = (temp) => {
    return ((temp - minTemp) / tempRange) * 100;
  };

  const handleRowClick = (index) => {
    setSelectedIndex(prev => prev === index ? null : index);
  };

  const formatFullDate = (dateString) => {
    // Parse date without timezone shift (same approach as weatherUtils.formatDate)
    let date;
    const isoMatch = String(dateString).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) {
      const [, y, m, d] = isoMatch;
      date = new Date(Number(y), Number(m) - 1, Number(d), 12, 0, 0);
    } else {
      date = new Date(dateString);
    }
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="weekly-forecast">
      <div className="card-header">
        <div className="title-section">
          <h2 className="card-title">
            <Icon name="calendar" size={18} /> 5-Day Forecast
          </h2>
          <span className="card-subtitle">{data.city}, {data.country}</span>
        </div>
        <div className="temp-range-label">
          <span className="range-low">{formatTemp(minTemp)}</span>
          <span className="range-separator">-</span>
          <span className="range-high">{formatTemp(maxTemp)}</span>
        </div>
      </div>

      <div className="forecast-list">
        {filteredDays.map((day, index) => {
          const isToday = index === 0;
          const isSelected = selectedIndex === index;
          const precipChance = Math.round(day.precipitation_chance);
          const lowPos = getTempPosition(day.temperature_min);
          const highPos = getTempPosition(day.temperature_max);

          return (
            <React.Fragment key={index}>
              <div
                className={`forecast-row ${isToday ? 'today' : ''} ${isSelected ? 'selected' : ''}`}
                onClick={() => handleRowClick(index)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleRowClick(index); }}
              >
                <div className="day-info">
                  <span className="day-name">{getDayName(day.date)}</span>
                  <span className="day-date">{formatDate(day.date, { weekday: undefined })}</span>
                </div>

                <div className="day-condition">
                  <img
                    src={getWeatherIcon(day.condition?.icon, '2x')}
                    alt={day.condition?.description || ''}
                    className="condition-icon"
                  />
                  <span className="condition-text">{day.condition?.main || 'N/A'}</span>
                </div>

                <div className="day-precip">
                  {precipChance > 0 ? (
                    <>
                      <Icon name="droplet" size={14} className="precip-icon" />
                      <span className="precip-value">{precipChance}%</span>
                    </>
                  ) : (
                    <span className="precip-none">-</span>
                  )}
                </div>

                <div className="day-temps">
                  <span className="temp-low">{formatTempShort(day.temperature_min)}</span>
                  
                  <div className="temp-bar-wrapper">
                    <div className="temp-bar-track">
                      <div
                        className="temp-bar-fill"
                        style={{
                          left: `${lowPos}%`,
                          width: `${highPos - lowPos}%`
                        }}
                      ></div>
                      <div
                        className="temp-bar-dot low"
                        style={{ left: `${lowPos}%` }}
                      ></div>
                      <div
                        className="temp-bar-dot high"
                        style={{ left: `${highPos}%` }}
                      ></div>
                    </div>
                  </div>
                  
                  <span className="temp-high">{formatTempShort(day.temperature_max)}</span>
                </div>

                <div className="day-wind">
                  <Icon name="wind" size={14} />
                  <span className="wind-value">{Math.round(day.wind_speed)} mph</span>
                </div>

                <div className="day-humidity">
                  <Icon name="droplet" size={14} />
                  <span className="humidity-value">{day.humidity}%</span>
                </div>

                <div className="row-expand-indicator">
                  <Icon name={isSelected ? 'chevronUp' : 'chevronDown'} size={14} />
                </div>
              </div>

              {/* Expanded detail panel */}
              {isSelected && (
                <div className="day-detail-panel">
                  <div className="day-detail-header">
                    <img
                      src={getWeatherIcon(day.condition?.icon, '2x')}
                      alt={day.condition?.description || ''}
                      className="day-detail-icon"
                    />
                    <div>
                      <span className="day-detail-date">{formatFullDate(day.date)}</span>
                      <span className="day-detail-condition">
                        {day.condition?.description
                          ? day.condition.description.charAt(0).toUpperCase() + day.condition.description.slice(1)
                          : day.condition?.main || ''}
                      </span>
                    </div>
                  </div>
                  <div className="day-detail-grid">
                    <div className="day-detail-item">
                      <Icon name="thermometer" size={14} />
                      <span className="day-detail-label">High</span>
                      <span className="day-detail-value high">{formatTemp(day.temperature_max)}</span>
                    </div>
                    <div className="day-detail-item">
                      <Icon name="thermometer" size={14} />
                      <span className="day-detail-label">Low</span>
                      <span className="day-detail-value low">{formatTemp(day.temperature_min)}</span>
                    </div>
                    <div className="day-detail-item">
                      <Icon name="droplet" size={14} />
                      <span className="day-detail-label">Humidity</span>
                      <span className="day-detail-value">{day.humidity}%</span>
                    </div>
                    <div className="day-detail-item">
                      <Icon name="wind" size={14} />
                      <span className="day-detail-label">Wind</span>
                      <span className="day-detail-value">{Math.round(day.wind_speed)} mph</span>
                    </div>
                    <div className="day-detail-item">
                      <Icon name="rain" size={14} />
                      <span className="day-detail-label">Precipitation</span>
                      <span className="day-detail-value">{precipChance}%</span>
                    </div>
                    <div className="day-detail-item">
                      <Icon name="thermometer" size={14} />
                      <span className="day-detail-label">Range</span>
                      <span className="day-detail-value">
                        {Math.round(day.temperature_max - day.temperature_min)}{DEGREE_SYMBOL}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      <div className="forecast-summary">
        <div className="summary-item">
          <span className="summary-label">Warmest</span>
          <span className="summary-value">{formatTemp(maxTemp)}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Coolest</span>
          <span className="summary-value">{formatTemp(minTemp)}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label">Avg Humidity</span>
          <span className="summary-value">
            {Math.round(filteredDays.reduce((a, d) => a + d.humidity, 0) / filteredDays.length)}%
          </span>
        </div>
      </div>
    </div>
  );
}

export default WeeklyForecast;
